import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Archive, ArchiveRestore, CalendarDays, Download, Loader2, LogOut, Mail, MapPin, Phone, Search, Sparkles } from "lucide-react";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeLabels = {
  maconnerie: "Maçonnerie générale",
  murs: "Construction de murs",
  fondations: "Fondations",
  dalles_beton: "Dalles et béton",
  renovation: "Rénovation",
  extension: "Extension de maison",
  ouverture: "Ouverture de mur porteur",
  gros_oeuvre: "Gros œuvre",
  amenagement: "Aménagement extérieur",
  beton_exterieur: "Béton / aménagement extérieur",
  autre: "Autre projet",
};

const budgetLabels = {
  moins_5000: "Moins de 5 000 €",
  "5000_15000": "5 000 – 15 000 €",
  "15000_50000": "15 000 – 50 000 €",
  plus_50000: "Plus de 50 000 €",
};

const statutLabels = {
  nouveau: "Nouveau",
  contacte: "Contacté",
  devis_envoye: "Devis envoyé",
  chantier_gagne: "Chantier gagné",
};

const statutClasses = {
  nouveau: "border-[#C4A484]/40 bg-[#C4A484]/10 text-[#C4A484]",
  contacte: "border-sky-400/30 bg-sky-400/10 text-sky-300",
  devis_envoye: "border-amber-400/30 bg-amber-400/10 text-amber-300",
  chantier_gagne: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300",
};

const statutOrder = ["nouveau", "contacte", "devis_envoye", "chantier_gagne"];

const formatDate = (value) => {
  if (!value) return "—";
  return new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
};

const toDateTimeLocal = (value) => {
  if (!value) return "";
  const date = new Date(value);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const getReminderState = (item) => {
  if (!item.rappel_at) return null;
  const date = new Date(item.rappel_at);
  if (item.rappel_email_sent_at) return { key: "envoye", label: "Rappel envoyé", className: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300" };
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  today.setHours(0, 0, 0, 0);
  tomorrow.setHours(0, 0, 0, 0);
  if (date < today) return { key: "retard", label: "Rappel en retard", className: "border-red-400/30 bg-red-400/10 text-red-300" };
  if (date < tomorrow) return { key: "aujourdhui", label: "Rappel aujourd'hui", className: "border-amber-400/30 bg-amber-400/10 text-amber-300" };
  return { key: "planifie", label: `Rappel le ${new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium" }).format(date)}`, className: "border-white/10 bg-white/5 text-zinc-400" };
};

const isCurrentMonth = (value) => {
  if (!value) return false;
  const date = new Date(value);
  const now = new Date();
  return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
};

const formatMonth = (monthKey) => {
  return new Intl.DateTimeFormat("fr-FR", { month: "long", year: "numeric" }).format(new Date(`${monthKey}-01T00:00:00`));
};

export default function AdminDevis() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("tous");
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [rappelDraft, setRappelDraft] = useState("");
  const [savingFollowUp, setSavingFollowUp] = useState(false);
  const [goalTarget, setGoalTarget] = useState(3);
  const [goalDraft, setGoalDraft] = useState("3");
  const [savingGoal, setSavingGoal] = useState(false);
  const [history, setHistory] = useState([]);

  const fetchHistory = useCallback(async () => {
    const res = await axios.get(`${API}/admin/history/monthly`, { withCredentials: true });
    setHistory(res.data.items || []);
  }, []);

  const load = useCallback(async () => {
    try {
      const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(me.data);
      const [res, goals, historyRes] = await Promise.all([
        axios.get(`${API}/admin/devis`, { withCredentials: true }),
        axios.get(`${API}/admin/settings/goals`, { withCredentials: true }),
        axios.get(`${API}/admin/history/monthly`, { withCredentials: true }),
      ]);
      setItems(res.data);
      setGoalTarget(goals.data.monthly_target);
      setGoalDraft(String(goals.data.monthly_target));
      setHistory(historyRes.data.items || []);
    } catch (err) {
      if (err.response?.status === 401) {
        try {
          const refreshed = await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
          setUser(refreshed.data);
          const [res, goals, historyRes] = await Promise.all([
            axios.get(`${API}/admin/devis`, { withCredentials: true }),
            axios.get(`${API}/admin/settings/goals`, { withCredentials: true }),
            axios.get(`${API}/admin/history/monthly`, { withCredentials: true }),
          ]);
          setItems(res.data);
          setGoalTarget(goals.data.monthly_target);
          setGoalDraft(String(goals.data.monthly_target));
          setHistory(historyRes.data.items || []);
          return;
        } catch {
          navigate("/admin/login", { replace: true });
          return;
        }
      }
      setError("Impossible de charger les demandes de devis.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const activeItems = useMemo(() => items.filter((item) => !item.archive), [items]);
  const archivedItems = useMemo(() => items.filter((item) => item.archive), [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const source = statusFilter === "archives" ? archivedItems : activeItems;
    return source.filter((item) => {
      const statut = item.statut || "nouveau";
      const reminder = getReminderState(item);
      if (statusFilter === "rappels" && !["retard", "aujourdhui"].includes(reminder?.key)) return false;
      if (!["tous", "rappels", "archives"].includes(statusFilter) && statut !== statusFilter) return false;
      if (!q) return true;
      return [item.nom, item.email, item.telephone, item.ville, item.type_projet, item.message].filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  }, [activeItems, archivedItems, search, statusFilter]);

  const statusCounts = useMemo(() => {
    return activeItems.reduce((acc, item) => {
      const statut = item.statut || "nouveau";
      acc[statut] = (acc[statut] || 0) + 1;
      return acc;
    }, {});
  }, [activeItems]);

  const reminderCount = useMemo(() => {
    return activeItems.filter((item) => ["retard", "aujourdhui"].includes(getReminderState(item)?.key)).length;
  }, [activeItems]);

  const pipeline = useMemo(() => {
    return statutOrder.map((statut) => {
      const count = statusCounts[statut] || 0;
      return {
        statut,
        label: statutLabels[statut],
        count,
        percent: activeItems.length ? Math.round((count / activeItems.length) * 100) : 0,
      };
    });
  }, [activeItems.length, statusCounts]);

  const conversionRate = activeItems.length ? Math.round(((statusCounts.chantier_gagne || 0) / activeItems.length) * 100) : 0;
  const monthlyWon = useMemo(() => {
    return activeItems.filter((item) => (item.statut || "nouveau") === "chantier_gagne" && isCurrentMonth(item.statut_updated_at || item.created_at)).length;
  }, [activeItems]);
  const goalPercent = goalTarget ? Math.min(100, Math.round((monthlyWon / goalTarget) * 100)) : 0;
  const goalRemaining = Math.max(0, goalTarget - monthlyWon);

  const openDetail = async (item) => {
    setSelected(item);
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/admin/devis/${item.id}`, { withCredentials: true });
      setSelected(res.data);
      setNoteDraft(res.data.note_privee || "");
      setRappelDraft(toDateTimeLocal(res.data.rappel_at));
    } catch {
      setError("Impossible de charger le détail de cette demande.");
    } finally {
      setDetailLoading(false);
    }
  };

  const saveGoal = async () => {
    const target = Number.parseInt(goalDraft, 10);
    if (!Number.isInteger(target) || target < 1 || target > 100 || savingGoal) return;
    setSavingGoal(true);
    setError("");
    try {
      const res = await axios.patch(`${API}/admin/settings/goals`, { monthly_target: target }, { withCredentials: true });
      setGoalTarget(res.data.monthly_target);
      setGoalDraft(String(res.data.monthly_target));
      fetchHistory().catch(() => {});
    } catch {
      setError("Impossible d'enregistrer l'objectif mensuel.");
    } finally {
      setSavingGoal(false);
    }
  };

  const exportCsv = () => {
    if (!filtered.length) return;
    const escapeCsv = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const headers = [
      "Date demande",
      "Nom",
      "Téléphone",
      "Email",
      "Ville",
      "Type de projet",
      "Budget",
      "Message",
      "Statut",
      "Note privée",
      "Rappel prévu",
      "Email rappel envoyé",
      "Résumé IA",
      "Urgence IA",
      "Budget estimé IA",
      "Conseil IA",
      "Archivé",
      "Date archivage",
    ];
    const rows = filtered.map((item) => [
      formatDate(item.created_at),
      item.nom,
      item.telephone,
      item.email,
      item.ville || "",
      typeLabels[item.type_projet] || item.type_projet || "",
      budgetLabels[item.budget] || item.budget || "",
      item.message || "",
      statutLabels[item.statut || "nouveau"],
      item.note_privee || "",
      item.rappel_at ? formatDate(item.rappel_at) : "",
      item.rappel_email_sent_at ? formatDate(item.rappel_email_sent_at) : "",
      item.ai?.resume || "",
      item.ai?.urgence || "",
      item.ai?.budget_estime || "",
      item.ai?.conseil || "",
      item.archive ? "Oui" : "Non",
      item.archive_at ? formatDate(item.archive_at) : "",
    ]);
    const csv = "\uFEFF" + [headers, ...rows].map((row) => row.map(escapeCsv).join(";")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `devis-dmz-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const saveFollowUp = async () => {
    if (!selected || savingFollowUp) return;
    setSavingFollowUp(true);
    setError("");
    try {
      const payload = {
        note_privee: noteDraft,
        rappel_at: rappelDraft ? new Date(rappelDraft).toISOString() : null,
      };
      const res = await axios.patch(`${API}/admin/devis/${selected.id}/suivi`, payload, { withCredentials: true });
      setSelected(res.data);
      setItems((current) => current.map((item) => (item.id === res.data.id ? res.data : item)));
    } catch {
      setError("Impossible d'enregistrer la note ou le rappel.");
    } finally {
      setSavingFollowUp(false);
    }
  };

  const updateStatus = async (statut) => {
    if (!selected || updatingStatus) return;
    setUpdatingStatus(true);
    try {
      const res = await axios.patch(`${API}/admin/devis/${selected.id}/statut`, { statut }, { withCredentials: true });
      setSelected(res.data);
      setItems((current) => current.map((item) => (item.id === res.data.id ? res.data : item)));
      fetchHistory().catch(() => {});
    } catch {
      setError("Impossible de mettre à jour le statut de cette demande.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const toggleArchive = async () => {
    if (!selected) return;
    setError("");
    try {
      const res = await axios.patch(`${API}/admin/devis/${selected.id}/archive`, { archive: !selected.archive }, { withCredentials: true });
      setItems((current) => current.map((item) => (item.id === res.data.id ? res.data : item)));
      setSelected(null);
      fetchHistory().catch(() => {});
    } catch {
      setError(selected.archive ? "Impossible de restaurer cette demande." : "Impossible d'archiver cette demande.");
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    navigate("/admin/login", { replace: true });
  };

  return (
    <>
      <SEO title="Demandes de devis | Administration DMZ Normandie" description="Gestion privée des demandes de devis DMZ Normandie." path="/admin/devis" robots="noindex,nofollow" />
      <section className="min-h-screen bg-[#0A0A0A] pt-32 pb-24 px-6 lg:px-10" data-testid="admin-devis-page">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
            <div>
              <p className="text-[10px] tracking-[0.5em] uppercase text-[#C4A484] mb-4">Administration</p>
              <h1 className="font-serif-display font-light text-4xl md:text-5xl text-[#F5F5F0]">Demandes de devis</h1>
              <p className="mt-3 text-sm font-light text-zinc-500">Connecté en tant que {user?.email || "administrateur"}</p>
            </div>
            <div className="flex flex-wrap gap-3 self-start lg:self-auto">
              <button onClick={exportCsv} disabled={!filtered.length} className="btn-outline-light" data-testid="admin-export-csv">
                <Download size={15} /> Exporter CSV ({filtered.length})
              </button>
              <button onClick={logout} className="btn-outline-light" data-testid="admin-logout-button">
                <LogOut size={15} /> Se déconnecter
              </button>
            </div>
          </div>

          <div className="card-dark p-5 md:p-6 mb-6" data-testid="admin-pipeline">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
              <div>
                <p className="text-[10px] tracking-[0.35em] uppercase text-[#C4A484] mb-2">Pipeline commercial</p>
                <p className="text-sm font-light text-zinc-500">{activeItems.length} demande(s) active(s), hors archives</p>
              </div>
              <div className="text-left md:text-right" data-testid="pipeline-conversion">
                <p className="font-serif-display font-light text-3xl text-[#F5F5F0]">{conversionRate}%</p>
                <p className="text-xs text-zinc-500">deviennent des chantiers gagnés</p>
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {pipeline.map((step) => (
                <div key={step.statut} className="border border-white/10 bg-white/[0.02] p-4" data-testid={`pipeline-statut-${step.statut}`}>
                  <div className="flex items-baseline justify-between gap-3 mb-3">
                    <p className="text-xs tracking-[0.15em] uppercase text-zinc-500">{step.label}</p>
                    <p className="font-serif-display text-2xl text-[#F5F5F0]">{step.count}</p>
                  </div>
                  <div className="h-1.5 bg-white/5 overflow-hidden">
                    <div className="h-full bg-[#C4A484] transition-all duration-500" style={{ width: `${step.percent}%` }} />
                  </div>
                  <p className="mt-2 text-[11px] text-zinc-600">{step.percent}% des demandes actives</p>
                </div>
              ))}
            </div>

            <div className="mt-5 border-t border-white/10 pt-5" data-testid="admin-goal-editor">
              <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
                <div className="flex-1">
                  <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
                    <p className="text-xs tracking-[0.2em] uppercase text-zinc-500">Objectif mensuel</p>
                    <p className="text-sm text-zinc-400" data-testid="pipeline-goal-count">
                      <span className="font-serif-display text-2xl text-[#F5F5F0]">{monthlyWon}</span> / {goalTarget} chantier(s) gagné(s) ce mois-ci
                    </p>
                  </div>
                  <div className="h-2 bg-white/5 overflow-hidden" data-testid="pipeline-goal-progress">
                    <div className="h-full bg-[#C4A484] transition-all duration-500" style={{ width: `${goalPercent}%` }} />
                  </div>
                  <p className="mt-2 text-xs font-light text-zinc-600">
                    {goalRemaining === 0 ? "Objectif atteint ce mois-ci." : `Encore ${goalRemaining} chantier(s) à gagner pour atteindre l'objectif.`}
                  </p>
                </div>
                <div className="flex items-end gap-3">
                  <div>
                    <label htmlFor="monthly-goal" className="block text-xs text-zinc-500 mb-2">Objectif / mois</label>
                    <input id="monthly-goal" type="number" min="1" max="100" value={goalDraft} onChange={(e) => setGoalDraft(e.target.value)} className="input-dark w-28" data-testid="admin-goal-input" />
                  </div>
                  <button type="button" onClick={saveGoal} disabled={savingGoal || Number(goalDraft) === goalTarget} className="btn-outline-light" data-testid="admin-goal-save">
                    {savingGoal ? <><Loader2 size={14} className="animate-spin" /> Enregistrement…</> : "Enregistrer"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="card-dark p-5 md:p-6 mb-6" data-testid="admin-monthly-history">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
              <div>
                <p className="text-[10px] tracking-[0.35em] uppercase text-[#C4A484] mb-2">Historique mensuel</p>
                <p className="text-sm font-light text-zinc-500">Demandes créées, chantiers gagnés et conversion par mois</p>
              </div>
              <p className="text-xs font-light text-zinc-600">12 derniers mois · archives exclues</p>
            </div>
            <div className="space-y-2">
              <div className="hidden md:grid grid-cols-[1fr_0.55fr_0.55fr_0.8fr_0.55fr] gap-4 px-3 pb-2 text-[10px] tracking-[0.2em] uppercase text-zinc-600 border-b border-white/10">
                <span>Mois</span>
                <span>Demandes</span>
                <span>Gagnés</span>
                <span>Conversion</span>
                <span>Objectif</span>
              </div>
              {history.map((entry) => (
                <div key={entry.month} className="grid grid-cols-2 md:grid-cols-[1fr_0.55fr_0.55fr_0.8fr_0.55fr] gap-4 items-center px-3 py-3 border border-white/5 bg-white/[0.015]" data-testid={`history-row-${entry.month}`}>
                  <p className="text-sm text-[#F5F5F0] capitalize">{formatMonth(entry.month)}</p>
                  <p className="text-sm text-zinc-400"><span className="md:hidden text-zinc-600">Demandes : </span>{entry.leads_created}</p>
                  <p className="text-sm text-zinc-400"><span className="md:hidden text-zinc-600">Gagnés : </span>{entry.chantiers_gagnes}</p>
                  <div>
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <span className="text-xs text-zinc-500">Conversion</span>
                      <span className="text-xs text-[#C4A484]">{entry.conversion_rate}%</span>
                    </div>
                    <div className="h-1.5 bg-white/5 overflow-hidden">
                      <div className="h-full bg-[#C4A484]" style={{ width: `${Math.min(100, entry.conversion_rate)}%` }} />
                    </div>
                  </div>
                  <p className="text-sm text-zinc-400"><span className="md:hidden text-zinc-600">Objectif : </span>{entry.monthly_target}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-5" data-testid="admin-status-filters">
            <button onClick={() => setStatusFilter("tous")} className={`px-4 py-2 text-xs tracking-[0.15em] uppercase border transition-colors ${statusFilter === "tous" ? "border-[#C4A484] bg-[#C4A484] text-[#0A0A0A]" : "border-white/10 text-zinc-400 hover:border-[#C4A484] hover:text-[#C4A484]"}`} data-testid="filter-statut-tous">
              Tous ({activeItems.length})
            </button>
            <button onClick={() => setStatusFilter("rappels")} className={`px-4 py-2 text-xs tracking-[0.15em] uppercase border transition-colors ${statusFilter === "rappels" ? "border-[#C4A484] bg-[#C4A484] text-[#0A0A0A]" : "border-white/10 text-zinc-400 hover:border-[#C4A484] hover:text-[#C4A484]"}`} data-testid="filter-rappels-a-traiter">
              Rappels à traiter ({reminderCount})
            </button>
            {statutOrder.map((statut) => (
              <button key={statut} onClick={() => setStatusFilter(statut)} className={`px-4 py-2 text-xs tracking-[0.15em] uppercase border transition-colors ${statusFilter === statut ? "border-[#C4A484] bg-[#C4A484] text-[#0A0A0A]" : "border-white/10 text-zinc-400 hover:border-[#C4A484] hover:text-[#C4A484]"}`} data-testid={`filter-statut-${statut}`}>
                {statutLabels[statut]} ({statusCounts[statut] || 0})
              </button>
            ))}
            <button onClick={() => setStatusFilter("archives")} className={`px-4 py-2 text-xs tracking-[0.15em] uppercase border transition-colors ${statusFilter === "archives" ? "border-[#C4A484] bg-[#C4A484] text-[#0A0A0A]" : "border-white/10 text-zinc-400 hover:border-[#C4A484] hover:text-[#C4A484]"}`} data-testid="filter-archives">
              Archivées ({archivedItems.length})
            </button>
          </div>

          <div className="relative mb-2">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher par nom, ville, projet, téléphone…" className="input-dark pl-11" data-testid="admin-search-input" />
          </div>
          <p className="mb-4 text-xs font-light text-zinc-600" data-testid="admin-export-help">L'export CSV reprend la recherche et le filtre actuellement actifs.</p>

          {error && items.length > 0 && <div className="mb-5 border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="admin-inline-error">{error}</div>}

          {loading ? (
            <div className="card-dark p-10 flex items-center gap-3 text-zinc-500" data-testid="admin-loading"><Loader2 size={17} className="animate-spin" /> Chargement des demandes…</div>
          ) : error && !items.length ? (
            <div className="card-dark p-10 text-red-300" data-testid="admin-error">{error}</div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-[0.9fr_1.1fr] gap-6">
              <div className="space-y-3" data-testid="admin-devis-list">
                {filtered.length === 0 ? (
                  <div className="card-dark p-8 text-zinc-500" data-testid="admin-empty-state">Aucune demande ne correspond à votre recherche.</div>
                ) : filtered.map((item) => {
                  const reminder = getReminderState(item);
                  return (
                  <button key={item.id} onClick={() => openDetail(item)} className={`w-full text-left card-dark p-5 transition-colors duration-300 ${selected?.id === item.id ? "border-[#C4A484]" : "hover:border-white/25"}`} data-testid={`admin-devis-item-${item.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h2 className="font-serif-display font-light text-2xl text-[#F5F5F0]">{item.nom}</h2>
                        <p className="mt-1 text-xs tracking-[0.15em] uppercase text-[#C4A484]">{typeLabels[item.type_projet] || item.type_projet}</p>
                        <span className={`inline-block mt-3 border px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase ${statutClasses[item.statut || "nouveau"]}`} data-testid={`statut-badge-${item.id}`}>
                          {statutLabels[item.statut || "nouveau"]}
                        </span>
                        {reminder && (
                          <span className={`inline-block mt-3 ml-2 border px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase ${reminder.className}`} data-testid={`rappel-badge-${item.id}`}>
                            {reminder.label}
                          </span>
                        )}
                        {item.archive && (
                          <span className="inline-block mt-3 ml-2 border border-zinc-500/30 bg-zinc-500/10 px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase text-zinc-400" data-testid={`archive-badge-${item.id}`}>
                            Archivée
                          </span>
                        )}
                      </div>
                      <span className="text-xs font-light text-zinc-600 shrink-0">{formatDate(item.created_at)}</span>
                    </div>
                    <p className="mt-3 text-sm font-light text-zinc-500 line-clamp-2">{item.message}</p>
                    <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs text-zinc-500">
                      {item.ville && <span className="flex items-center gap-1.5"><MapPin size={12} className="text-[#C4A484]" /> {item.ville}</span>}
                      <span className="flex items-center gap-1.5"><Phone size={12} className="text-[#C4A484]" /> {item.telephone}</span>
                    </div>
                  </button>
                  );
                })}
              </div>

              <div className="card-dark p-6 md:p-8 xl:sticky xl:top-24 self-start min-h-[360px]" data-testid="admin-devis-detail">
                {!selected ? (
                  <div className="h-full min-h-[320px] flex items-center justify-center text-center text-zinc-600 font-light" data-testid="admin-detail-placeholder">
                    Sélectionnez une demande pour voir le détail, les photos et l'analyse IA.
                  </div>
                ) : detailLoading ? (
                  <div className="h-full min-h-[320px] flex items-center justify-center gap-3 text-zinc-500" data-testid="admin-detail-loading"><Loader2 size={17} className="animate-spin" /> Chargement du détail…</div>
                ) : (
                  <div>
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 border-b border-white/10 pb-6 mb-6">
                      <div>
                        <h2 className="font-serif-display font-light text-3xl text-[#F5F5F0]">{selected.nom}</h2>
                        <p className="mt-2 text-xs tracking-[0.2em] uppercase text-[#C4A484]">{typeLabels[selected.type_projet] || selected.type_projet}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <a href={`tel:${selected.telephone}`} className="btn-outline-light" data-testid="admin-call-button"><Phone size={14} /> Appeler</a>
                        <a href={`mailto:${selected.email}`} className="btn-sable" data-testid="admin-email-button"><Mail size={14} /> Répondre</a>
                        <button type="button" onClick={toggleArchive} className="btn-outline-light" data-testid="admin-archive-toggle">
                          {selected.archive ? <><ArchiveRestore size={14} /> Restaurer</> : <><Archive size={14} /> Archiver</>}
                        </button>
                      </div>
                    </div>

                    <div className="mb-6" data-testid="admin-status-editor">
                      <p className="text-[10px] tracking-[0.35em] uppercase text-zinc-600 mb-3">Statut de suivi</p>
                      <div className="flex flex-wrap gap-2">
                        {statutOrder.map((statut) => (
                          <button key={statut} onClick={() => updateStatus(statut)} disabled={updatingStatus} className={`px-4 py-2 text-xs tracking-[0.15em] uppercase border transition-colors ${(selected.statut || "nouveau") === statut ? "border-[#C4A484] bg-[#C4A484] text-[#0A0A0A]" : "border-white/10 text-zinc-400 hover:border-[#C4A484] hover:text-[#C4A484]"}`} data-testid={`set-statut-${statut}`}>
                            {statutLabels[statut]}
                          </button>
                        ))}
                      </div>
                      {updatingStatus && <p className="mt-3 text-xs text-zinc-500 flex items-center gap-2" data-testid="statut-updating"><Loader2 size={13} className="animate-spin" /> Enregistrement…</p>}
                    </div>

                    <div className="border border-white/10 bg-white/[0.02] p-5 mb-6" data-testid="admin-followup-editor">
                      <p className="text-[10px] tracking-[0.35em] uppercase text-zinc-600 mb-4">Suivi privé</p>
                      <label htmlFor="note-privee" className="block text-xs text-zinc-500 mb-2">Note privée</label>
                      <textarea id="note-privee" rows={3} maxLength={2000} value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} placeholder="Ex. rappeler après réception du plan, vérifier le budget…" className="input-dark resize-none" data-testid="admin-note-input" />
                      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 mt-4 md:items-end">
                        <div>
                          <label htmlFor="rappel-at" className="block text-xs text-zinc-500 mb-2">Date et heure du rappel</label>
                          <input id="rappel-at" type="datetime-local" value={rappelDraft} onChange={(e) => setRappelDraft(e.target.value)} className="input-dark" data-testid="admin-rappel-input" />
                        </div>
                        <button type="button" onClick={saveFollowUp} disabled={savingFollowUp} className="btn-sable" data-testid="admin-save-followup">
                          {savingFollowUp ? <><Loader2 size={15} className="animate-spin" /> Enregistrement…</> : "Enregistrer le suivi"}
                        </button>
                      </div>
                      {selected.rappel_email_sent_at && (
                        <p className="mt-3 text-xs text-emerald-300" data-testid="admin-rappel-sent">Email de rappel envoyé le {formatDate(selected.rappel_email_sent_at)}</p>
                      )}
                      <p className="mt-3 text-xs font-light text-zinc-600">Le rappel choisi est visible ici et envoyé automatiquement par email le jour prévu.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-testid="admin-detail-fields">
                      <p className="text-sm text-zinc-400 flex items-center gap-2"><Phone size={14} className="text-[#C4A484]" /> {selected.telephone}</p>
                      <p className="text-sm text-zinc-400 flex items-center gap-2"><Mail size={14} className="text-[#C4A484]" /> {selected.email}</p>
                      <p className="text-sm text-zinc-400 flex items-center gap-2"><MapPin size={14} className="text-[#C4A484]" /> {selected.ville || "Ville non renseignée"}</p>
                      <p className="text-sm text-zinc-400 flex items-center gap-2"><CalendarDays size={14} className="text-[#C4A484]" /> {formatDate(selected.created_at)}</p>
                    </div>

                    <div className="mb-6">
                      <p className="text-[10px] tracking-[0.35em] uppercase text-zinc-600 mb-3">Message</p>
                      <p className="text-sm font-light text-zinc-300 leading-relaxed whitespace-pre-line" data-testid="admin-message">{selected.message}</p>
                      <p className="mt-4 text-xs text-zinc-500">Budget : {budgetLabels[selected.budget] || selected.budget || "Non renseigné"}</p>
                    </div>

                    {selected.ai && (
                      <div className="border border-[#C4A484]/25 bg-[#C4A484]/5 p-5 mb-6" data-testid="admin-ai-summary">
                        <p className="flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-[#C4A484] mb-4"><Sparkles size={13} /> Analyse IA</p>
                        <div className="space-y-3 text-sm font-light text-zinc-300">
                          <p><span className="text-zinc-600">Résumé :</span> {selected.ai.resume || "—"}</p>
                          <p><span className="text-zinc-600">Urgence :</span> {selected.ai.urgence || "—"}</p>
                          <p><span className="text-zinc-600">Budget estimé :</span> {selected.ai.budget_estime || "—"}</p>
                          <p><span className="text-zinc-600">Conseil :</span> {selected.ai.conseil || "—"}</p>
                        </div>
                      </div>
                    )}

                    {selected.photos?.length > 0 && (
                      <div>
                        <p className="text-[10px] tracking-[0.35em] uppercase text-zinc-600 mb-3">Photos du client</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="admin-client-photos">
                          {selected.photos.map((photo, i) => (
                            <img key={`${selected.id}-${i}`} src={photo} alt={`Photo client ${i + 1}`} className="w-full h-28 object-cover border border-white/10" />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
